<script setup>
/**
 * Renders a WhatsApp-style phone bubble preview of a template message.
 * Used in the template send modal to show agents exactly what the
 * contact will receive. Adapts to variable substitution in real-time.
 */
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  BUTTON_TYPES,
  findComponentByType,
  replaceTemplateVariables,
  renderLiquidContactVariables,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: { type: Object, required: true },
  processedParams: { type: Object, default: () => ({}) },
  sampleContact: { type: Object, default: null },
  businessName: { type: String, default: '' },
  categoryLabel: { type: String, default: '' },
  pointOfView: { type: String, default: 'customer' },
  introNotice: { type: String, default: '' },
  showIntroNotice: { type: Boolean, default: false },
  showVerifiedBadge: { type: Boolean, default: true },
  showReadReceipts: { type: Boolean, default: false },
  osStyle: { type: String, default: '' },
});

const { t } = useI18n();

const headerComponent = computed(() =>
  findComponentByType(props.template, COMPONENT_TYPES.HEADER)
);
const bodyComponent = computed(() =>
  findComponentByType(props.template, COMPONENT_TYPES.BODY)
);
const footerComponent = computed(() =>
  findComponentByType(props.template, COMPONENT_TYPES.FOOTER)
);
const buttonsComponent = computed(() =>
  findComponentByType(props.template, COMPONENT_TYPES.BUTTONS)
);

const renderPreviewText = text =>
  renderLiquidContactVariables(text, props.sampleContact);

const headerText = computed(() => {
  if (!headerComponent.value || headerComponent.value.format !== 'TEXT')
    return '';
  const raw = headerComponent.value.text || '';
  return replaceTemplateVariables(raw, props.processedParams, {
    componentType: 'header',
    sampleContact: props.sampleContact,
  });
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const mediaUrl = computed(() => props.processedParams?.header?.media_url || '');

const bodyText = computed(() => {
  const raw = bodyComponent.value?.text || '';
  return replaceTemplateVariables(raw, props.processedParams, {
    sampleContact: props.sampleContact,
  });
});

const footerText = computed(() =>
  renderPreviewText(footerComponent.value?.text || '')
);

function iconForButton(type) {
  const map = {
    [BUTTON_TYPES.URL]: 'i-lucide-external-link',
    [BUTTON_TYPES.PHONE_NUMBER]: 'i-lucide-phone',
    [BUTTON_TYPES.COPY_CODE]: 'i-lucide-copy',
    [BUTTON_TYPES.QUICK_REPLY]: 'i-lucide-reply',
    [BUTTON_TYPES.FLOW]: 'i-lucide-git-branch',
    [BUTTON_TYPES.ORDER_DETAILS]: 'i-lucide-receipt',
  };
  return map[type] || 'i-lucide-reply';
}

const buttons = computed(() => {
  if (!buttonsComponent.value?.buttons) return [];
  const mapped = buttonsComponent.value.buttons.map(btn => ({
    text: btn.text,
    type: btn.type,
    icon: iconForButton(btn.type),
  }));
  if (mapped.length > 3) {
    return [
      ...mapped.slice(0, 2),
      {
        text: t('WHATSAPP_TEMPLATES.PREVIEW.SEE_ALL_OPTIONS'),
        type: 'overflow',
        icon: 'i-lucide-list',
      },
    ];
  }
  return mapped;
});

const previewTime = new Date().toLocaleTimeString([], {
  hour: '2-digit',
  minute: '2-digit',
});

const previewDate = new Date().toLocaleDateString([], {
  month: 'short',
  day: 'numeric',
});

const businessName = computed(() => {
  if (props.businessName) return props.businessName;
  const name = props.template?.name?.replace(/_/g, ' ') || 'WhatsApp Business';
  return name.charAt(0).toUpperCase() + name.slice(1);
});

const categoryLabel = computed(
  () =>
    props.categoryLabel ||
    props.template?.category ||
    t('WHATSAPP_TEMPLATES.PREVIEW.CATEGORY')
);

const hasPreviewContent = computed(
  () => hasMediaHeader.value || headerText.value || bodyText.value
);

const isBusinessPointOfView = computed(() => props.pointOfView === 'business');

const messageBubbleClass = computed(() =>
  isBusinessPointOfView.value
    ? 'ml-auto rounded-tr-sm bg-[#D9FDD3]'
    : 'rounded-tl-sm bg-white'
);

const messageWidthClass = computed(() =>
  isBusinessPointOfView.value ? 'w-[90%]' : 'w-[88%]'
);

const introNoticeText = computed(
  () => props.introNotice || t('WHATSAPP_TEMPLATES.PREVIEW.INTRO_NOTICE')
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-3 text-xs">
      <div class="flex items-center gap-1.5 text-n-slate-11">
        <Icon icon="i-lucide-eye" class="size-3.5" />
        <span class="font-medium">{{
          t('WHATSAPP_TEMPLATES.PREVIEW.TITLE')
        }}</span>
      </div>
      <span
        class="px-2 py-1 font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
      >
        {{ categoryLabel }}
      </span>
    </div>

    <div class="flex justify-center">
      <div
        class="w-full max-w-[21rem] rounded-[2rem] bg-[#111B21] p-2 shadow-lg"
      >
        <div
          class="overflow-hidden rounded-[1.55rem] bg-[#F0F2F5] dark:bg-n-solid-2"
        >
          <div
            class="relative flex items-center justify-between px-4 py-2 bg-[#075E54]"
          >
            <span
              v-if="osStyle === 'ios'"
              class="absolute left-1/2 top-1.5 h-1.5 w-16 -translate-x-1/2 rounded-full bg-black/35"
            />
            <span class="text-[11px] font-semibold text-white">
              {{ previewTime }}
            </span>
            <div class="flex items-center gap-1.5 text-white/90">
              <Icon icon="i-lucide-signal" class="size-3" />
              <Icon icon="i-lucide-wifi" class="size-3" />
              <Icon icon="i-lucide-battery-full" class="size-3.5" />
            </div>
          </div>

          <div class="flex items-center gap-3 px-4 py-3 bg-[#075E54]">
            <div
              class="flex items-center justify-center rounded-full size-9 bg-white/15 text-white"
            >
              <Icon icon="i-lucide-building-2" class="size-4" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5 min-w-0">
                <span class="text-sm font-semibold text-white truncate">
                  {{ businessName }}
                </span>
                <Icon
                  v-if="showVerifiedBadge"
                  icon="i-lucide-badge-check"
                  class="flex-shrink-0 size-3.5 text-[#9FE3CF]"
                />
              </div>
              <span class="block text-xs text-white/75 truncate">
                {{ t('WHATSAPP_TEMPLATES.PREVIEW.BUSINESS_ACCOUNT') }}
              </span>
            </div>
            <Icon icon="i-lucide-more-vertical" class="size-4 text-white/80" />
          </div>

          <div
            class="relative min-h-[28rem] bg-[#EFE7DD] dark:bg-n-solid-3 px-4 py-4"
          >
            <div class="mb-4 text-center">
              <span
                class="inline-flex px-2 py-1 text-[11px] font-medium rounded-md bg-white/80 text-[#667781]"
              >
                {{ previewDate }}
              </span>
            </div>

            <div v-if="showIntroNotice" class="mb-3 text-center">
              <div
                class="inline-flex max-w-[17rem] items-start gap-1.5 rounded-md bg-[#FFF3C4] px-2.5 py-2 text-left text-[11px] leading-4 text-[#3B4A54] shadow-sm"
              >
                <Icon
                  icon="i-lucide-lock-keyhole"
                  class="mt-0.5 size-3 shrink-0 text-[#54656F]"
                />
                <span>{{ introNoticeText }}</span>
              </div>
            </div>

            <div
              class="rounded-lg shadow-sm border border-black/5 overflow-hidden"
              :class="[messageWidthClass, messageBubbleClass]"
            >
              <div v-if="hasMediaHeader">
                <img
                  v-if="headerComponent?.format === 'IMAGE' && mediaUrl"
                  :src="mediaUrl"
                  alt=""
                  class="object-cover w-full max-h-44"
                />
                <div
                  v-else
                  class="flex flex-col items-center justify-center gap-1.5 py-8 text-[#667781] bg-[#F0F2F5]"
                >
                  <Icon
                    :icon="
                      headerComponent?.format === 'VIDEO'
                        ? 'i-lucide-video'
                        : headerComponent?.format === 'DOCUMENT'
                          ? 'i-lucide-file-text'
                          : 'i-lucide-image'
                    "
                    class="size-7"
                  />
                  <span class="text-xs">{{ headerComponent?.format }}</span>
                </div>
              </div>

              <div class="p-3">
                <p
                  v-if="headerText"
                  class="text-[13px] font-semibold leading-snug text-[#111B21]"
                >
                  {{ headerText }}
                </p>

                <p
                  v-if="bodyText"
                  class="text-[13px] leading-5 whitespace-pre-line text-[#111B21] mt-1"
                >
                  {{ bodyText }}
                </p>

                <p
                  v-if="!hasPreviewContent"
                  class="text-[13px] leading-5 text-[#667781]"
                >
                  {{ t('WHATSAPP_TEMPLATES.PREVIEW.EMPTY') }}
                </p>

                <div class="flex items-end justify-between mt-2">
                  <p v-if="footerText" class="text-[11px] text-[#667781]">
                    {{ footerText }}
                  </p>
                  <span
                    class="inline-flex items-center gap-1 text-[11px] text-[#667781] ml-auto shrink-0"
                  >
                    {{ previewTime }}
                    <Icon
                      v-if="showReadReceipts"
                      icon="i-lucide-check-check"
                      class="size-3 text-[#53BDEB]"
                    />
                  </span>
                </div>
              </div>

              <div v-if="buttons.length" class="border-t border-[#E7EDF2]">
                <div
                  v-for="(button, index) in buttons"
                  :key="index"
                  class="flex items-center justify-center gap-1.5 px-3 py-2.5 text-[13px] text-[#0086CB] font-medium border-t border-[#E7EDF2] first:border-t-0"
                >
                  <Icon
                    v-if="button.icon"
                    :icon="button.icon"
                    class="size-3.5"
                  />
                  <span class="truncate">{{ button.text }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 px-3 py-2 bg-[#F0F2F5]">
            <div
              class="flex items-center justify-center rounded-full size-8 text-[#667781]"
            >
              <Icon icon="i-lucide-smile" class="size-4" />
            </div>
            <div
              class="flex items-center min-w-0 flex-1 h-9 px-3 rounded-full bg-white text-[#667781]"
            >
              <p class="text-xs truncate">
                {{ t('WHATSAPP_TEMPLATES.PREVIEW.REPLY_PLACEHOLDER') }}
              </p>
            </div>
            <Icon icon="i-lucide-mic" class="size-4 text-[#667781]" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
